import { useMemo, useCallback, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import type {
	Business,
	BusinessQuery
} from '@/modules/dashboard/dashboardTypes';
import { dashboardSelectBusiness } from '@/modules/dashboard/dashboardActions';
import { cn } from '@/lib/classNames';
import {
	Menu,
	XIcon,
	DropdownArrowDown,
	CloseThin
} from '@/resources/assets/images';
import { useIsScreenWidthSmallerThan } from '@/hooks/useIsScreenWidthSmallerThan';

import { Filters, useFiltersContext } from '@/components/molecules/Filters';
import type { User } from '@/modules/user/userTypes';

import { sendAnalytics } from '@/services/analyticsService';
import { useMenuToggle } from './src/hooks/useMenuToggle';
import { useListDetailsFiltersContext } from '../../../providers/ListDetailsFiltersProviders';

export const BusinessFilter = () => {
	const { appliedFilters, onFiltersChange } = useListDetailsFiltersContext();

	const menuRef = useRef<HTMLDivElement>(null);

	const { isMenuOpening, isMenuOpen, handleMenuToggle } =
		useMenuToggle(menuRef);

	const dispatch = useDispatch();

	const selectBusiness = useCallback(
		(business: Business | null) =>
			dispatch(dashboardSelectBusiness(business)),
		[dispatch]
	);

	const filtersCount = useMemo(() => {
		const initialFiltersCount =
			(appliedFilters?.searches?.length ?? 0) +
			(appliedFilters?.lead !== undefined ? 1 : 0);

		const countGroups = [
			['coordinates', 'country'],
			['contactedYes', 'contactedNo'],
			['scheduledYes', 'scheduledNo'],
			['includeEmailsYes'],
			['includeFBMessenger'],
			['includeIGMessenger'],
			['includePhoneNumber'],
			['includeWebsitesYes', 'includeWebsitesNo'],
			['chains', 'nonChains'],

			['countOne'],
			['countTwoTen'],
			['countTenPlus'],

			['minYelpReviews', 'maxYelpReviews'],
			['includePOIs', 'excludePOIs'],

			['salesVolumeLessThan5M'],
			['salesVolume5To10M'],
			['salesVolume10To20M'],
			['salesVolume20To50M'],
			['salesVolume50To100M'],
			['salesVolumeOver100M'],

			['employees1To49'],
			['employees50To99'],
			['employees100To249'],
			['employees250To499'],
			['employeesOver500'],

			['sqftSmall'],
			['sqftMedium'],
			['sqftLarge']
		];

		return countGroups.reduce((count, group) => {
			const isGroupActive = group.some(
				key => appliedFilters?.[key as keyof BusinessQuery]
			);

			if (isGroupActive) {
				return count + 1;
			}

			return count;
		}, initialFiltersCount);
	}, [appliedFilters]);

	const handleChangeFilters = useCallback(
		(filters: Omit<BusinessQuery, 'skip' | 'limit'>) => {
			onFiltersChange({
				skip: 0,
				limit: 50,
				...filters
			});

			selectBusiness(null);
		},
		[onFiltersChange, selectBusiness]
	);

	const { isSmaller: isMobile } = useIsScreenWidthSmallerThan(600);

	return (
		<div
			ref={menuRef}
			className={cn(
				'relative flex w-full flex-col items-center overflow-visible rounded-lg bg-dm-black',
				'lg:h-full lg:min-h-0 lg:w-full lg:max-w-64 lg:items-start'
			)}
		>
			<button
				type="button"
				className="flex w-full items-center gap-4 rounded-md border border-dm-mid-grey bg-dm-black p-2 lg:hidden lg:h-0 lg:w-0"
				onClick={handleMenuToggle}
			>
				{isMenuOpen ? (
					<MenuOpenIcon isMobile={isMobile} />
				) : (
					<Menu className="size-6 fill-dm-white" />
				)}

				<span className="inline-flex items-center gap-2 text-sm text-dm-white">
					Filters
					{filtersCount > 0 && (
						<span className="flex aspect-square h-5 w-auto items-center justify-center rounded-md border border-dm-gold text-sm text-dm-gold">
							{filtersCount}
						</span>
					)}
				</span>
			</button>

			<nav
				data-is-menu-open={isMenuOpen}
				data-is-menu-opening={isMenuOpening}
				className={cn(
					'absolute left-0 top-0 z-50 flex h-0 w-full translate-y-9 flex-col gap-12 p-px transition-all',
					'data-[is-menu-opening=true]:overflow-hidden',
					'data-[is-menu-open=true]:h-auto data-[is-menu-open=true]:min-w-[70vw]',
					'lg:relative lg:h-full lg:w-full lg:translate-y-0 lg:justify-between',
					"before:absolute before:bottom-0 before:left-0 before:right-0 before:top-0 before:origin-top-left before:scale-0 before:rounded-xl before:bg-dm-black1/[.98] before:transition before:content-['']",
					'data-[is-menu-open=true]:before:scale-100 data-[is-menu-open=true]:before:rounded-md data-[is-menu-open=true]:before:border data-[is-menu-open=true]:before:border-dm-gray',
					'lg:before:content-none lg:data-[is-menu-open=true]:h-full lg:data-[is-menu-open=true]:min-w-0' // reset mobile state on large screens
				)}
			>
				<Filters.Root>
					<section
						data-is-menu-open={isMenuOpen}
						className={cn(
							'relative hidden h-full w-full flex-1 flex-col items-center gap-4 overflow-auto',
							'data-[is-menu-open=true]:flex',
							'lg:flex lg:gap-0'
						)}
					>
						<span className="hidden h-14 w-full items-center gap-2.5 border-b border-dm-mid-grey px-4 text-lg text-dm-white lg:inline-flex">
							Filters
							{filtersCount > 0 && (
								<div className="flex items-center justify-center gap-0.5 rounded-md border border-dm-gold text-center">
									<p className="pointer-events-none flex h-7 min-w-7 items-center justify-center text-sm font-bold">
										{filtersCount}
									</p>

									<span className="inline-block h-7 w-px flex-1 border-l border-dm-gold align-middle" />

									<ClearFiltersButton
										appliedFilters={appliedFilters}
										onChangeFilters={handleChangeFilters}
									/>
								</div>
							)}
						</span>

						<div className="flex w-full flex-col">
							<Filters.Search
								appliedFilters={appliedFilters}
								onFilterChange={handleChangeFilters}
							/>
							<Filters.ContactStatus
								appliedFilters={appliedFilters}
								onFilterChange={handleChangeFilters}
							/>
						</div>
					</section>
				</Filters.Root>
			</nav>
		</div>
	);
};

type MenuOpenIconProps = {
	isMobile: boolean;
};

const MenuOpenIcon = ({ isMobile }: MenuOpenIconProps) => {
	if (isMobile) {
		return <DropdownArrowDown className="size-6 fill-dm-white" />;
	}

	return <XIcon className="size-6 fill-dm-white" />;
};

type ClearFiltersButtonProps = {
	appliedFilters: BusinessQuery | undefined;
	onChangeFilters: (businessQuery: BusinessQuery) => void;
};

const ClearFiltersButton = ({
	appliedFilters,
	onChangeFilters
}: ClearFiltersButtonProps) => {
	const { user } = useSelector<
		RootState,
		{
			user: User;
		}
	>(
		state => ({
			user: state.user.getIn(['authorize', 'user']).toJS()
		}),
		shallowEqual
	);

	const { handleClearLocation } = useFiltersContext();

	const handleClearFilters = () => {
		onChangeFilters({
			minDistance: 0,
			maxDistance: 40,
			limit: 50,
			skip: 0,
			sortField:
				user?.features?.businessSortingTestVariant === 'control'
					? 'distance'
					: 'score',
			sort:
				user?.features?.businessSortingTestVariant === 'control'
					? { distance: '1' }
					: { score: '-1' },
			count: true
		});

		handleClearLocation();

		sendAnalytics({
			type: 'track',
			userId: user.email,
			properties: {
				appliedFilters
			},
			eventName: 'User cleared all filters'
		});
	};

	return (
		<button
			className="flex aspect-square h-7 min-w-7 cursor-pointer items-center justify-center"
			type="button"
			onClick={handleClearFilters}
		>
			<CloseThin className="h-2.5 w-2.5 [&_path]:stroke-dm-gold" />
		</button>
	);
};
